import type { RemovableRef, UseStorageOptions } from '@vueuse/core'

import { useLocalStorage, useSessionStorage } from '@vueuse/core'

/**
 * 缓存存储实例，避免重复创建
 * 分别缓存 localStorage 和 sessionStorage
 */
const localStorageCache: Record<string, RemovableRef<any>> = {}
const sessionStorageCache: Record<string, RemovableRef<any>> = {}

/**
 * 获取或创建 localStorage 实例
 *
 * @param key 存储键名
 * @param defaultValue 默认值
 * @param options 存储选项
 * @returns RemovableRef 实例
 *
 * @example
 * ```ts
 * // 简单值
 * const count = getCachedLocalStorage('count', 0)
 *
 * // 对象类型（自动使用 JSON 序列化）
 * const user = getCachedLocalStorage<User>('user', null)
 *
 * // 自定义序列化器
 * const data = getCachedLocalStorage('data', null, {
 *   serializer: {
 *     read: (v) => customDeserialize(v),
 *     write: (v) => customSerialize(v)
 *   }
 * })
 * ```
 */
export function getCachedLocalStorage<T>(
  key: string,
  defaultValue: T,
  options?: UseStorageOptions<T>,
): RemovableRef<T> {
  if (!localStorageCache[key]) {
    // 如果默认值是对象且没有提供自定义序列化器，使用 JSON 序列化
    const storageOptions = options || (
      typeof defaultValue === 'object' && defaultValue !== null
        ? {
            serializer: {
              read: (v: string) => (v ? JSON.parse(v) : defaultValue),
              write: (v: T) => JSON.stringify(v),
            },
          }
        : undefined
    )

    localStorageCache[key] = useLocalStorage<T>(key, defaultValue, storageOptions)
  }
  return localStorageCache[key]
}

/**
 * 获取或创建 sessionStorage 实例
 *
 * @param key 存储键名
 * @param defaultValue 默认值
 * @param options 存储选项
 * @returns RemovableRef 实例
 *
 * @example
 * ```ts
 * // 简单值
 * const tempData = getCachedSessionStorage('temp', '')
 *
 * // 对象类型（自动使用 JSON 序列化）
 * const template = getCachedSessionStorage<PipeTemplate>('template', null)
 *
 * // 自定义序列化器
 * const data = getCachedSessionStorage('data', null, {
 *   serializer: {
 *     read: (v) => customDeserialize(v),
 *     write: (v) => customSerialize(v)
 *   }
 * })
 * ```
 */
export function getCachedSessionStorage<T>(
  key: string,
  defaultValue: T,
  options?: UseStorageOptions<T>,
): RemovableRef<T> {
  if (!sessionStorageCache[key]) {
    // 如果默认值是对象且没有提供自定义序列化器，使用 JSON 序列化
    const storageOptions = options || (
      typeof defaultValue === 'object' && defaultValue !== null
        ? {
            serializer: {
              read: (v: string) => (v ? JSON.parse(v) : defaultValue),
              write: (v: T) => JSON.stringify(v),
            },
          }
        : undefined
    )

    sessionStorageCache[key] = useSessionStorage<T>(key, defaultValue, storageOptions)
  }
  return sessionStorageCache[key]
}

/**
 * 清除指定键的缓存实例
 * 注意：这只是清除内存中的缓存引用，不会删除存储中的数据
 *
 * @param key 存储键名
 * @param type 存储类型
 */
export function clearStorageCache(key: string, type: 'local' | 'session' = 'local'): void {
  if (type === 'local') {
    delete localStorageCache[key]
  }
  else {
    delete sessionStorageCache[key]
  }
}

/**
 * 清除所有缓存实例
 * 注意：这只是清除内存中的缓存引用，不会删除存储中的数据
 */
export function clearAllStorageCache(): void {
  Object.keys(localStorageCache).forEach(key => delete localStorageCache[key])
  Object.keys(sessionStorageCache).forEach(key => delete sessionStorageCache[key])
}
